import {
  EnvTypes,
  EvalEnv,
  Plugins,
  Variable,
  withSystem,
} from '@packages/systems/wfdl';
import {TText, TTypeConstructor, functionOf} from '@packages/systems/wfdl-ast';
import {identifier, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {weakMemo} from '@packages/utilities/memo';
import {type Lazy, force} from '@packages/utilities/lazy';
import type {State} from '@packages/shared/render/plugins/Basic/dataTypes/Link/LinkTypes';
import {cleanHref} from '@packages/systems/core/utils/HtmlUtils';

const TBasicLink = TTypeConstructor(
  qualify(Plugins.name('Basic'), typeName('Link'))
);

// Basic::Link values arrive in several shapes: file-mode links carry
// either `assetUrl` (CMS-hydrated) or just `assetId` (property/page
// bindings); page-mode links carry `pageId` and need the page tree to
// produce a slug; section-mode links carry `domId` (a WFDL element
// UUID) and need the rendered element's `attr.id` to produce an anchor
// fragment. Curry the converter over a `LinkUrlResolvers` bundle so
// render-context callers (canvas, SSR) can resolve the id-only shapes
// to real URLs via addBasicLinkUtilsToEnv
//
// `sanitizeHref` runs once at the outer return; the canonical impl is
// `hrefAttr` in @packages/systems/core/utils/HtmlUtils, which is
// runtime-environment-aware and can't be default-baked here.
export type LinkUrlResolvers = {
  resolveAssetUrl?: (id: string) => string | undefined;
  resolvePageUrl?: (pageId: string) => string | undefined;
  resolveSectionId?: (domId: string) => string | undefined;
  sanitizeHref?: (href: string) => string;
};

const noopResolvers: LinkUrlResolvers = {};

// Picks the URL-ish field for the link's mode -- mirrors the legacy
// `{{wf {...}}}` behaviour of emitting just the href the user would
// otherwise type by hand. Returns the raw href; the outer wrapper
// applies sanitizeHref once.
const resolveRawHref = (
  resolvers: LinkUrlResolvers,
  data: Partial<State>
): string => {
  switch (data.mode) {
    case 'email':
      return data.email ?? '';
    case 'tel':
      return data.tel ?? '';
    case 'section': {
      if (!data.domId) return '';
      const attrId = resolvers.resolveSectionId?.(data.domId);
      return attrId ? `#${attrId}` : '';
    }
    case 'file': {
      if (data.assetUrl) return data.assetUrl;
      const resolved = data.assetId
        ? resolvers.resolveAssetUrl?.(data.assetId)
        : undefined;
      return resolved ?? '';
    }
    case 'page':
    case 'dynpage': {
      const slug = data.pageSlug || data.url;
      if (slug) return slug;
      const resolved = data.pageId
        ? resolvers.resolvePageUrl?.(data.pageId)
        : undefined;
      return resolved ?? '';
    }
    default:
      // Mirror the Link atom render which runs external mode link URLs
      // through cleanHref so that the URL string output by this link-to-text
      // conversion matches the href attribute from an equivalent Link atom.
      return data.url ? cleanHref(data.url) : '';
  }
};

const _linkToText = {
  envType: {
    dataType: functionOf(withSystem({isBindable: true}, TBasicLink), TText),
    exported: 'hidden' as const,
    implicit: 'prefilledArgs' as const,
  },
  // Hidden so a Basic::Link -> Text adapter doesn't appear in every
  // Text binding picker product-wide. The embed editor whitelists it
  // via enableCmsImplicitsForEmbed so component property Link bindings
  // become reachable when targeting Text in code embeds.
  value:
    (resolvers: LinkUrlResolvers) =>
    (link: unknown): string => {
      if (!link || typeof link !== 'object') return '';
      const href = resolveRawHref(resolvers, link as Partial<State>);
      if (!href) return '';
      return resolvers.sanitizeHref?.(href) ?? href;
    },
} as const;

export const linkToText: EnvTypes.UnsafeTypedValue = {
  envType: _linkToText.envType,
  value: _linkToText.value(noopResolvers),
};

const LINK_TO_TEXT_VARIABLE = Variable.qualified(
  qualify(Plugins.name('Basic'), identifier('linkToText'))
);

// Re-binds Basic::linkToText with concrete resolvers so render-context
// callers can produce real urls for property/page-binding link values
// that arrive as `{assetId}` only. Nested weakMemo keeps env identity
// stable across renders without running object equality on this hot path.
const addBasicLinkUtilsToEnvForEvalEnv = weakMemo((env: EvalEnv.EvalEnvType) =>
  weakMemo((resolvers: LinkUrlResolvers) =>
    EvalEnv.insertValue(
      env,
      LINK_TO_TEXT_VARIABLE,
      _linkToText.value(resolvers)
    )
  )
);

export const addBasicLinkUtilsToEnv = (
  env: EvalEnv.EvalEnvType,
  resolvers: LinkUrlResolvers
): EvalEnv.EvalEnvType => addBasicLinkUtilsToEnvForEvalEnv(env)(resolvers);

// pageId-to-href map -> resolver. weakMemo keyed on map reference; see
// addBasicLinkUtilsToEnv for the cache-stability rationale. Designer
// already produces this map via PageStore's generateHrefMap.
export const createPageUrlResolver = weakMemo(
  (hrefMap: Record<string, string>) =>
    (pageId: string): string | undefined =>
      hrefMap[pageId]
);

// Lazy elementId-to-attrId map -> resolver. The map is taken lazily so
// the underlying tree walk (collectIdAttributes / buildIdAttrMap) only
// runs the first time a section-mode link is actually evaluated.
export const createSectionIdResolver = weakMemo(
  (idAttrMap: Lazy<Record<string, string>>) =>
    (domId: string): string | undefined =>
      force(idAttrMap)[domId]
);
